import React from 'react'
import type { Product } from '../../../types/ProductListing'
import type { VariantStock } from '../StockUpdateModal'

interface MobileStockUpdateViewProps {
  product: Product
  variants: VariantStock[]
  onStockChange: (variantId: string, warehouseId: string, newStock: number) => void
  onSave: () => void
  onCancel: () => void
  loading: boolean
  error?: string | null
}

const MobileStockUpdateView: React.FC<MobileStockUpdateViewProps> = ({
  product,
  variants,
  onStockChange,
  onSave,
  onCancel,
  loading,
  error
}) => {
  const handleStockInputChange = (
    variantId: string,
    warehouseId: string,
    value: string
  ) => {
    const numValue = parseInt(value)
    if (!isNaN(numValue) && numValue >= 0) {
      onStockChange(variantId, warehouseId, numValue)
    } else if (value === '') {
      onStockChange(variantId, warehouseId, 0)
    }
  }

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[140] tw-flex tw-flex-col tw-bg-white dark:tw-bg-slate-900">
      {/* Header */}
      <header className="tw-shrink-0 tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-200 dark:tw-border-slate-800 tw-bg-white dark:tw-bg-slate-900 tw-p-4">
        <button
          type="button"
          className="tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-slate-800 dark:tw-text-slate-200 hover:tw-bg-slate-100 dark:hover:tw-bg-slate-800 tw-transition-colors"
          onClick={onCancel}
        >
          <span className="material-symbols-outlined tw-text-2xl">arrow_back</span>
        </button>
        <h1 className="tw-flex-1 tw-text-center tw-text-lg tw-font-bold tw-tracking-tight tw-text-slate-900 dark:tw-text-slate-50">
          Manage Stock
        </h1>
        <div className="tw-w-10"></div>
      </header>

      {/* Main Content */}
      <main className="tw-flex-1 tw-overflow-y-auto">
        {variants.map((variant) => (
          <div 
            key={variant.variantId}
            className="tw-border-b tw-border-slate-200 dark:tw-border-slate-800"
          >
            {/* Product Info */}
            <div className="tw-bg-white dark:tw-bg-slate-800 tw-p-4">
              <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                <div 
                  className="tw-aspect-square tw-h-16 tw-w-16 tw-shrink-0 tw-rounded-lg tw-bg-cover tw-bg-center tw-bg-no-repeat"
                  style={{
                    backgroundImage: `url('${variant.image || 'https://via.placeholder.com/64x64?text=No+Image'}')`
                  }}
                ></div>
                <div className="tw-flex-1">
                  <p className="tw-text-base tw-font-medium tw-leading-tight tw-text-slate-900 dark:tw-text-slate-50">
                    {variant.variantName}
                  </p>
                  {variant.sku && (
                    <p className="tw-text-sm tw-font-normal tw-leading-normal tw-text-slate-500 dark:tw-text-slate-400">
                      SKU: {variant.sku}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Warehouse Stock Details */}
            <div className="tw-flex tw-flex-col tw-gap-4 tw-bg-slate-50 dark:tw-bg-slate-900 tw-p-4">
              {variant.warehouses.map((warehouse) => (
                <div 
                  key={warehouse.warehouseId}
                  className="tw-flex tw-items-center tw-justify-between tw-gap-4"
                >
                  <div>
                    <p className="tw-text-sm tw-font-medium tw-leading-tight tw-text-slate-800 dark:tw-text-slate-200">
                      {warehouse.warehouseName}
                    </p>
                    <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                      Current: {warehouse.currentStock}
                    </p>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-text-slate-900 dark:tw-text-slate-50">
                    <label className="tw-text-sm tw-font-medium tw-text-slate-600 dark:tw-text-slate-300">
                      Ready:
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={warehouse.readyStock}
                      onChange={(e) =>
                        handleStockInputChange(
                          variant.variantId,
                          warehouse.warehouseId,
                          e.target.value
                        )
                      }
                      className="tw-w-14 tw-rounded-md tw-border tw-border-slate-300 dark:tw-border-slate-700 tw-bg-white dark:tw-bg-slate-800 tw-py-1.5 tw-text-center tw-text-sm tw-font-medium tw-leading-normal focus:tw-border-primary focus:tw-ring-1 focus:tw-ring-primary tw-outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:tw-appearance-none [&::-webkit-outer-spin-button]:tw-appearance-none [-moz-appearance:textfield]"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </main>

      {/* Footer */}
      <footer className="tw-border-t tw-border-slate-200 dark:tw-border-slate-800 tw-bg-white dark:tw-bg-slate-900 tw-p-4 tw-shrink-0">
        {error && (
          <div className="tw-mb-3 tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md">
            <p className="tw-text-sm tw-text-red-600">{error}</p>
          </div>
        )}
        <div className="tw-flex tw-w-full tw-items-center tw-gap-3">
          <button 
            onClick={onCancel}
            disabled={loading}
            className="tw-flex tw-h-12 tw-flex-1 tw-items-center tw-justify-center tw-rounded-xl tw-bg-slate-200 dark:tw-bg-slate-800 tw-text-base tw-font-bold tw-text-slate-900 dark:tw-text-slate-50 disabled:tw-opacity-50 tw-transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={onSave}
            disabled={loading}
            className="tw-flex tw-h-12 tw-flex-1 tw-items-center tw-justify-center tw-rounded-xl tw-bg-primary tw-text-base tw-font-bold tw-text-white disabled:tw-opacity-50 tw-transition-colors"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </footer>
    </div>
  )
}

export default MobileStockUpdateView